import * as moment from "moment";
import { League } from "./model/League";
import { IMatchList, IMatch, ISettingsService } from "../typings/typings";
import { IFilterService, ITimeoutService, IQService } from "angular";
import { DialogService } from "./DialogService";
import { LeagueHandler } from "../services/api/eslapi/LeagueHandler";
import { Base } from "./config/contestant_state/Base";
import { InProgress } from "./config/contestant_state/InProgress";
import { CheckIn } from "./config/contestant_state/CheckIn";
import { Setup } from "./config/contestant_state/Setup";
import { PostSignUp } from "./config/contestant_state/PostSignUp";
import { Finished } from "./config/contestant_state/Finished";
import { LateSignUp } from "./config/contestant_state/LateSignUp";
import { SignUp } from "./config/contestant_state/SignUp";
import { PreSignUp } from "./config/contestant_state/PreSignUp";
import { SharedContestantStateService } from "./SharedContestantStateService";
import { LeagueMatchResource } from "../services/api/eslapi/LeagueMatchResource";
import { LeagueFactory } from "./model/LeagueFactory";
import { Store } from "@ngrx/store";
import {
    RootState,
    getLeague,
    getContestant,
    LoadLeagueOnceAction,
    LoadContestantsAction,
    LoadContestantsOnceAction,
    LoadLeagueAction,
    entryAvailable,
    exists,
} from "@play/state";
import { LeagueService } from "ng-league";
import { getSelectedContestant } from "@play/league-widget";
import { Observable, combineLatest, interval } from "rxjs";
import { ContestantInfo, Match } from "@play/api-typings";
import { switchMap, first, startWith, tap, filter, map } from "rxjs/operators";
import { isTeam } from "@play/utility/league";

export class LeagueInteraction {
    public league: League;
    public matches: IMatchList;
    public currentMatch: IMatch;
    public contestant;
    public currentState;
    public opponent;

    public state;
    public loggedIn: boolean;

    private refreshCounter;

    constructor(
        public $scope,
        public $filter: IFilterService,
        public LeagueMatchResource: LeagueMatchResource,
        public $timeout: ITimeoutService,
        public SharedContestantStateService: SharedContestantStateService,
        public SettingsService: ISettingsService,
        public LeagueHandler: LeagueHandler,
        public LeagueService: LeagueService,
        public $q: IQService,
        public DialogService: DialogService,
        private store: Store<RootState>
    ) {
        const leagueId = +this.SettingsService.leagueId;
        const userId = this.SettingsService.user ? +this.SettingsService.user.id : null;
        this.loggedIn = Boolean(userId);

        this.contestant = { state: "not_signed_up" };
        this.currentState = new Base(this.contestant.state);

        this.store.dispatch(new LoadLeagueOnceAction(leagueId));

        if (userId) {
            this.store.dispatch(new LoadContestantsOnceAction(leagueId, userId));
        }

        const league$ = this.store.select(getLeague(leagueId)).pipe(filter(exists));

        const contestant$: Observable<ContestantInfo> = league$.pipe(
            filter(entryAvailable),
            switchMap(league => {
                const { entry } = league;
                const contestantType = isTeam(entry) ? "team" : "player";

                if (!userId) {
                    return [null];
                }

                return this.store.pipe(
                    getSelectedContestant(entry.id, contestantType),
                    switchMap(contestantId => this.store.select(getContestant(entry.id, userId, contestantId)))
                );
            })
        );

        combineLatest(contestant$, league$)
            .pipe(
                tap(([contestant, league]) => {
                    if (!contestant) {
                        this.contestant.state = this.currentState.CONTESTANT_STATE_NOT_SIGNED_UP;
                        this.currentState = new Base(this.contestant.state);
                    } else if (league.entry) {
                        this.contestant = { ...contestant };
                    }

                    if (league.error || !league.entry) {
                        this.league = undefined;
                    } else {
                        this.league = LeagueFactory.getLeagueByType(league.entry);
                    }

                    if (league.entry) {
                        this.initState();
                    }
                }),
                switchMap(([contestant, league]) => {
                    if (!league.entry) {
                        return [];
                    }

                    if (!contestant) {
                        return [];
                    }

                    return [contestant];
                }),
                first(),
                // Since contestant and league has been found, reload data every 60 seconds
                switchMap(contestant =>
                    interval(60000).pipe(
                        startWith(0),
                        map(() => contestant)
                    )
                )
            )
            .subscribe((contestant: ContestantInfo) => {
                if (contestant.state !== "finished") {
                    this.refreshMatchData();
                }

                this.refreshResultData();
            });
    }

    public refreshLeagueData() {
        const leagueId = +this.SettingsService.leagueId;
        const userId = this.SettingsService.user ? +this.SettingsService.user.id : null;

        this.store.dispatch(new LoadLeagueAction(leagueId, true));

        if (userId) {
            this.store.dispatch(new LoadContestantsAction(leagueId, userId, true));
        }
    }

    public resetError() {
        this.$scope.error = "";
    }

    public getSignUpTitleClass() {
        if (this.currentState.getActiveTitle() == "signup") {
            return "active";
        } else {
            return "done";
        }
    }

    public getCheckinTitleClass(): string {
        if (this.league && this.league.isCheckInAvailable()) {
            if (this.currentState.getActiveTitle() == "signup") {
                return "";
            } else if (this.currentState.getActiveTitle() == "checkin") {
                return "active";
            }
        }
        return "done";
    }

    public getPlayTitleClass(): string {
        if (this.currentState.getActiveTitle() == "signup") {
            return "";
        } else if (this.currentState.getActiveTitle() == "checkin") {
            return "";
        } else {
            return "active";
        }
    }

    public getUrlByTemplate(template): string {
        return this.league.getUrlByTemplate(template, this.$filter, this.$scope);
    }

    public userHasTeamPermissions(): boolean {
        return Boolean(this.contestant);
    }

    public getSecondsUntilLeagueBegin(): number {
        if (!this.league) {
            return 0;
        }

        let date = this.league.getStartDate();
        if (typeof date !== "object") {
            return 0;
        }

        return this.league.getSecondsUntilDate(date);
    }

    public getSecondsUntilSignUpBegin(): number {
        let dateString = 0;

        if (this.league.timeline && this.league.timeline.signUp) {
            dateString = this.league.timeline.signUp.begin;
        }

        return this.league.getSecondsUntilDate(new Date(dateString));
    }

    public getSecondsUntilNextPhase(): number {
        let date = this.league.getNextDate();
        if (typeof date !== "object") {
            return 0;
        }

        return this.league.getSecondsUntilDate(date);
    }

    public userHasMatch(): boolean {
        return typeof this.currentMatch !== "undefined";
    }

    public userHasChallenge(): boolean {
        return (
            typeof this.matches !== "undefined" &&
            this.matches.hasOwnProperty("challenge") &&
            this.matches.challenge.length > 0 &&
            typeof this.matches.challenge[0] !== "undefined"
        );
    }

    public userHasResults(): boolean {
        return typeof this.$scope.results !== "undefined" && this.$scope.results.length > 0;
    }

    public userNumResults(): number {
        if (!this.userHasResults()) {
            return 0;
        }

        return this.$scope.results.length;
    }

    public userNumLosses(): number {
        if (!this.userHasResults()) {
            return 0;
        }

        return this.$scope.results.filter(result => {
            return result.myResult() === "loss";
        }).length;
    }

    public getSecondsUntilNextUpdate(): number {
        return this.league.getSecondsUntilNextUpdate();
    }

    public initState() {
        this.league.setCurrentContestant(this.contestant);
        this.league.setCurrentState(this.currentState);

        let leagueState = this.league.getState();
        if (this.contestant.state === "playing") {
            leagueState = "inProgress";
        }

        switch (leagueState) {
            case "setup": {
                this.currentState = new Setup(this.contestant.state);
                break;
            }
            case "preSignUp": {
                this.currentState = new PreSignUp(this.contestant.state);
                break;
            }
            case "signUp": {
                this.currentState = new SignUp(this.contestant.state);
                break;
            }
            case "postSignUp": {
                this.currentState = new PostSignUp(this.contestant.state);
                break;
            }
            case "lateSignUp": {
                this.currentState = new LateSignUp(this.contestant.state);
                break;
            }
            case "checkIn": {
                this.currentState = new CheckIn(this.contestant.state);
                break;
            }
            case "inProgress": {
                this.currentState = new InProgress(this.contestant.state);
                break;
            }
            case "finished": {
                this.currentState = new Finished(this.contestant.state);
                break;
            }
            default: {
                this.currentState = new Base(this.contestant.state);
            }
        }

        this.league.setCurrentState(this.currentState);
        this.resetError();

        if (this.league.hasCountDown()) {
            this.activateTimelineStateChange();
        }
    }

    private filterNonContestantMatches(data: any[]) {
        return data.reduce((matches: Match[], match: Match) => {
            const inMatch = match.contestants.find(c => +c.team.id === +this.contestant.id);

            return inMatch ? [...matches, match] : matches;
        }, []);
    }

    private refreshMatchData() {
        this.league.refreshMatchData(this.league, this.$scope.user, this.contestant, this.LeagueMatchResource, this.$q).then(data => {
            if (data.length > 0) {
                if (this.league.contestantType === "team") {
                    data = this.filterNonContestantMatches(data);
                }

                data = this.LeagueHandler.sortMatchesByStartDate(data);
                this.matches = {
                    open: [],
                    challenge: [],
                    closed: [],
                    protest: [],
                };

                for (let idx in data) {
                    if (data.hasOwnProperty(idx)) {
                        let match: IMatch = data[idx];
                        this.matches[match.status].push(match);
                    }
                }

                this.currentMatch = this.league.getCurrentMatch(this.matches);

                if (typeof this.currentMatch === "object") {
                    if (this.currentMatch.status === "open" || this.currentMatch.status === "challenge") {
                        this.LeagueMatchResource.getCurrentResult(this.currentMatch.id).then(matchResult => {
                            this.currentMatch.setResult(matchResult);
                        });

                        this.LeagueMatchResource.getCurrentSetup(this.currentMatch.id).then(matchSetup => {
                            this.currentMatch.setSetup(matchSetup);
                        });
                    }
                } else {
                    this.currentMatch = undefined;
                }
            } else {
                this.currentMatch = undefined;
            }
        });
    }

    private refreshResultData(): void {
        if (!this.league) {
            return;
        }

        this.league.refreshResultData(this.league, this.$scope.user, this.contestant, this.LeagueMatchResource, this.$q).then(data => {
            if (data && data.length && this.league.contestantType === "team") {
                data = this.filterNonContestantMatches(data);
            }

            this.$scope.results = this.LeagueHandler.sortMatchesByStartDateDesc(data);
        });
    }

    private activateTimelineStateChange(): void {
        let mseconds = this.getSecondsUntilNextUpdate() * 1000;

        // Stop ticking
        // delay bug: if delay is bigger than 2^31, it will be called immediately
        // see http://stackoverflow.com/questions/21922892/angularjs-timeout-max-value
        if (typeof mseconds !== "number" || isNaN(mseconds) || mseconds <= 0 || mseconds >= 2147483647) {
            return;
        }

        if (!this.refreshCounter) {
            this.refreshCounter = this.$timeout(
                () => {
                    this.refreshCounter = null;
                    this.refreshLeagueData();
                },
                mseconds,
                true
            );
        }
    }

    /**
     * Check if the instant challenger is active right now
     * @return boolean
     */
    public isInstantChallengerActive(): boolean {
        if (this.league.isInstantChallengerActive()) {
            let o = this.LeagueHandler.getTimeTillInstantChallengerChanges(this.league) as { state: any };
            return o.hasOwnProperty("state") && o.state === "open";
        }

        return false;
    }

    /**
     * Return the amount of hours till instantchallenger opens/closes
     * Positive integer if the IC is closed stating the interval of hours till it opens, negative till it closes
     *
     * @return number|null
     */
    public getInstantChallengerSlotTime(): number {
        let now = moment();
        let o = this.LeagueHandler.getTimeTillInstantChallengerChanges(this.league) as { state: any; next: any };

        // next property is null, no enddate for the whole period (see getTimeTillInstantChallengerChanges for more info)
        if (o.hasOwnProperty("state") && o.state === "open" && o.hasOwnProperty("next") && o.next === null) {
            return null;
        }

        let time = moment(o.next);
        if (o.hasOwnProperty("state") && o.state === "closed") {
            // get hours till this point of time
            return (time.unix() - now.unix()) / 60 / 60;
        } else if (o.hasOwnProperty("state") && o.state === "open") {
            if (time.date() > now.date()) {
                return null;
            } else {
                return ((time.unix() - now.unix()) / 60 / 60) * -1;
            }
        }
    }
}
